import { useState } from 'react';
import {
  usePreisToolDisposition,
  usePreisToolDispositionSettings,
  useSetReorderPoint,
} from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolDisposition } from '@/api/preis-tool/preis-tool.types';
import {
  DsAlert,
  DsButton,
  DsInput,
  DsSegmented,
} from '@/modules/internal-v2/ds';
import { fmtSharePct, fmtTargetMargin } from '../disposition-settings';
import { fmtEUR, fmtPct } from '../helpers';
import { DispositionSettingsDialog } from './disposition-settings-dialog.component';
import { SalesHistoryDialog } from './sales-history-dialog.component';

interface DispositionBlockProps {
  itemId: string;
  itemName: string;
  /**
   * Whether the user may set the reorder point and the settings
   * (`PREIS_TOOL_MANAGE`).
   */
  canEdit: boolean;
}

const pieces = (n: number) => n.toLocaleString('de-DE');

/**
 * Purchasing aid of one item (EOS-62), always visible between the metric
 * strip and the offer table: how long the free stock lasts, when to reorder,
 * how much, and up to which purchase price the target margin holds. Figures
 * only — no comparison with the offers and no buy / wait verdict.
 */
export function DispositionBlock({
  itemId,
  itemName,
  canEdit,
}: DispositionBlockProps) {
  // `null` = none chosen: the default of the settings applies (EOS-62).
  const [chosenMargin, setChosenMargin] = useState<number | null>(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data: settings } = usePreisToolDispositionSettings();
  // The target margins are set for everyone: a chosen one that is no longer
  // in the list falls back to the default.
  const targetMargin =
    chosenMargin != null &&
    (settings == null || settings.targetMargins.includes(chosenMargin))
      ? chosenMargin
      : undefined;
  const { data, isPending, isError } = usePreisToolDisposition(
    itemId,
    targetMargin,
  );

  const a = data?.assumptions;
  const margins = settings?.targetMargins ?? a?.targetMargins ?? [];
  const hasSales = (data?.soldQty ?? 0) > 0;

  return (
    <div className="pt-dispo-block">
      <div className="pt-dispo-head">
        <span className="title">Einkaufshilfe</span>
        <span className="pt-label">Zielmarge</span>
        {margins.length > 0 && (
          <DsSegmented
            ariaLabel="Zielmarge"
            size="small"
            options={margins.map((m) => ({ value: m, label: fmtTargetMargin(m) }))}
            value={
              targetMargin ??
              settings?.defaultTargetMargin ??
              data?.targetMargin ??
              null
            }
            onChange={setChosenMargin}
          />
        )}
        {a && (
          <DsButton
            size="small"
            variant="ghost"
            onClick={() => setSalesOpen(true)}
          >
            Verkäufe der letzten {a.salesPeriodDays} Tage
          </DsButton>
        )}
        <DsButton
          size="small"
          variant="ghost"
          onClick={() => setExplainOpen(!explainOpen)}
        >
          {explainOpen ? 'Rechenweg ausblenden' : 'So wird gerechnet'}
        </DsButton>
        <div className="hint">
          Kennzahlen zur Einkaufsentscheidung · keine automatische Empfehlung
        </div>
        <DsButton
          size="small"
          variant="ghost"
          onClick={() => setSettingsOpen(true)}
          title={
            canEdit
              ? 'Werte der Einkaufshilfe für alle einstellen'
              : 'Werte der Einkaufshilfe ansehen'
          }
        >
          <span aria-hidden="true">{'\u2699\uFE0E'}</span> Einstellungen
        </DsButton>
      </div>

      {isPending ? (
        <div className="pt-fix-empty">Lade…</div>
      ) : isError || !data || !a ? (
        <div className="pt-fix-empty">Fehler beim Laden der Einkaufshilfe.</div>
      ) : (
        <>
          {data.salesAreDistorted && (
            <DsAlert severity="warn">
              Ein einzelner Auftrag macht{' '}
              {fmtPct(data.largestOrderQty / data.soldQty, 0)} des Absatzes aus
              ({pieces(data.largestOrderQty)} von {pieces(data.soldQty)} Stück).
              Absatz pro Tag, Reichweite und der Vorschlag für den Meldebestand
              können dadurch irreführen.{' '}
              <button
                className="ds-btn ghost small"
                onClick={() => setSalesOpen(true)}
              >
                Verkäufe anzeigen
              </button>
            </DsAlert>
          )}

          <div className="pt-dispo-grid">
            <Tile
              label="Frei"
              value={pieces(data.freeStock)}
              sub={
                data.freeStockIsCritical
                  ? 'unter dem Meldebestand'
                  : `${pieces(data.stock)} Lager − ${pieces(data.committed)} gebunden`
              }
              tone={data.freeStockIsCritical ? 'critical' : undefined}
            />
            <Tile
              label="Absatz pro Tag"
              value={
                hasSales
                  ? data.salesPerDay.toLocaleString('de-DE', {
                      maximumFractionDigits: 2,
                    })
                  : '0'
              }
              sub={
                hasSales
                  ? `${pieces(data.soldQty)} Stk in ${a.salesPeriodDays} Tagen · ${data.orderCount} Auftr.`
                  : `kein Verkauf in ${a.salesPeriodDays} Tagen`
              }
              onClick={() => setSalesOpen(true)}
              title="Verkäufe des Zeitraums anzeigen"
            />
            <Tile
              label="Reichweite"
              value={
                data.rangeDays == null ? '—' : `${pieces(data.rangeDays)} Tage`
              }
              sub={
                data.rangeDays == null
                  ? 'ohne Absatz nicht berechenbar'
                  : 'freier Bestand ÷ Absatz pro Tag'
              }
            />
            <ReorderPointTile data={data} canEdit={canEdit} />
            <Tile
              label={`Nachkauf · ${a.reorderHorizonDays} Tage`}
              value={pieces(data.reorderQty)}
              sub={
                data.onOrder > 0
                  ? `${pieces(data.onOrder)} Stk bereits bestellt`
                  : 'empfohlene Menge'
              }
            />
            <Tile
              label="Kauflimit"
              value={
                data.purchaseLimit == null
                  ? '—'
                  : `€ ${fmtEUR(data.purchaseLimit)}`
              }
              sub={
                data.purchaseLimit == null
                  ? 'kein Standard-VK gepflegt – keine Kalkulation möglich'
                  : `höchster EK bei ${fmtTargetMargin(data.targetMargin)} auf € ${fmtEUR(data.standardPrice)}`
              }
              tone={data.purchaseLimit == null ? 'muted' : undefined}
            />
          </div>

          {explainOpen && (
            <div className="pt-dispo-explain">
              <ul>
                <li>
                  Frei = Lagerbestand − gebundene Menge (offene Kundenaufträge).
                </li>
                <li>
                  Absatz pro Tag = verkaufte Stückzahl der letzten{' '}
                  {a.salesPeriodDays} Tage ÷ {a.salesPeriodDays}. Gezählt werden
                  die Positionen der SAP-Kundenaufträge nach Belegdatum,
                  stornierte Aufträge nicht.
                </li>
                <li>Reichweite = freier Bestand ÷ Absatz pro Tag.</li>
                <li>
                  Meldebestand (Vorschlag) = Absatz pro Tag × (Lieferzeit +{' '}
                  {a.safetyDays} Tage Sicherheit), aufgerundet. Lieferzeit:{' '}
                  {data.leadTimeDays} Tage
                  {data.leadTimeIsDefault
                    ? ' (Standardwert aus den Einstellungen – das günstigste Angebot nennt keine)'
                    : ' (aus dem günstigsten Angebot)'}
                  . Ein von Hand gesetzter Meldebestand ersetzt den Vorschlag.
                </li>
                <li>
                  Nachkauf = Absatz pro Tag × {a.reorderHorizonDays} Tage +
                  Meldebestand − freier Bestand − offene Bestellungen,
                  mindestens 0.
                </li>
                <li>
                  Kauflimit = Standard-Verkaufspreis ÷ (1 + Zielmarge). Zielmarge:{' '}
                  {fmtTargetMargin(data.targetMargin)}
                  {data.targetMargin === a.defaultTargetMargin
                    ? ' (Standard)'
                    : ''}
                  .
                </li>
                <li>
                  Hinweis auf verzerrten Absatz: ein einzelner Auftrag macht
                  mindestens {fmtSharePct(a.outlierShare)} der verkauften Menge
                  aus.
                </li>
              </ul>
              <div className="pt-fix-hint">
                Die Werte (Tage, Anteil, Zielmargen) gelten für alle und sind
                unter „Einstellungen“ einstellbar; die Formeln bleiben gleich.
              </div>
            </div>
          )}
        </>
      )}

      {salesOpen && (
        <SalesHistoryDialog
          itemId={itemId}
          itemName={itemName}
          onClose={() => setSalesOpen(false)}
        />
      )}
      {settingsOpen && (
        <DispositionSettingsDialog
          canEdit={canEdit}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
  onClick,
  title,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'critical' | 'muted';
  /** Makes the whole tile a button. */
  onClick?: () => void;
  title?: string;
}) {
  const Box = onClick ? 'button' : 'div';
  return (
    <Box
      className={`pt-metric${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      title={title}
    >
      <div className="label">{label}</div>
      <div
        className="value"
        style={{
          color:
            tone === 'critical'
              ? 'var(--neg)'
              : tone === 'muted'
                ? 'var(--ink-3)'
                : undefined,
        }}
      >
        {value}
      </div>
      <div className="sub" title={sub}>
        {sub}
      </div>
    </Box>
  );
}

/** Reorder point: the suggestion, overridable by hand and stored per item. */
function ReorderPointTile({
  data,
  canEdit,
}: {
  data: PreisToolDisposition;
  canEdit: boolean;
}) {
  const setReorderPoint = useSetReorderPoint();
  const [text, setText] = useState(String(data.reorderPoint));
  // Follow the stored value when it changes (other item, saved, reset).
  const [shown, setShown] = useState(data.reorderPoint);
  if (data.reorderPoint !== shown) {
    setShown(data.reorderPoint);
    setText(String(data.reorderPoint));
  }

  const commit = () => {
    const trimmed = text.trim();
    if (!/^\d+$/.test(trimmed)) {
      setText(String(data.reorderPoint));
      return;
    }
    const value = Number(trimmed);
    if (value !== data.reorderPoint)
      setReorderPoint.mutate({ itemId: data.itemId, reorderPoint: value });
  };

  return (
    <div className="pt-metric">
      <div className="label">
        Meldebestand
        {data.reorderPointIsManual && (
          <span
            className="ds-flag info"
            title="Von Hand gesetzt; der Vorschlag wird nicht verwendet."
          >
            manuell
          </span>
        )}
      </div>
      <div className="value">
        {canEdit ? (
          <DsInput
            mono
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            inputMode="numeric"
            style={{ width: 90 }}
            aria-label="Meldebestand"
          />
        ) : (
          pieces(data.reorderPoint)
        )}
      </div>
      <div className="sub">
        Vorschlag {pieces(data.suggestedReorderPoint)}
        {canEdit && data.reorderPointIsManual && (
          <button
            className="ds-btn ghost small"
            style={{ marginLeft: 6 }}
            onClick={() =>
              setReorderPoint.mutate({
                itemId: data.itemId,
                reorderPoint: null,
              })
            }
            disabled={setReorderPoint.isPending}
          >
            übernehmen
          </button>
        )}
      </div>
    </div>
  );
}
